import { SubflowMessengerAction } from 'lib-shared-types-api';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';

import { FlowMessengerAction } from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import {
  useFlowData,
  UseFlowValue,
} from 'components/settings/components/sections/dixaMessenger/views/flow/hooks';
import { withFlows, WithFlowsInjectedPropsT } from 'hocs';
import { useAppPathParams } from 'utils/url/useAppPathParams';

type FlowContextT = Omit<UseFlowValue, 'localCopyOfFlow'> & {
  initialMainFlow: UseFlowValue['localCopyOfFlow']; // "localCopyOfFlow" renamed to "initialMainFlow"
  isLoading: boolean;
  flowId: string | undefined;
  update: WithFlowsInjectedPropsT<
    FlowMessengerAction,
    SubflowMessengerAction
  >['flows']['update'];
  highlightedNodes: SubflowMessengerAction[];
  setHighlightedNodes: Dispatch<SetStateAction<SubflowMessengerAction[] | []>>;
  navigateAwayPromptEnabled: boolean;
  setNavigateAwayPromptEnabled: (enabled: boolean) => void;
};

export const FlowContext = createContext<FlowContextT>({} as FlowContextT);

/**
 * This component is the root of the flow editor. It provides the context for
 * the flow editor and its sub-components. The need for this component stems
 * from the fact that in order to edit the subflows to a flow, we need to able
 * to navigate back and forth between the main flow editor and the subflow
 * editor. This component provides the context for the flow editor and its
 * sub-components to be able to do that.
 */
export const FlowEditorRoot = withFlows<
  FlowMessengerAction,
  WithFlowsInjectedPropsT<FlowMessengerAction, SubflowMessengerAction>,
  SubflowMessengerAction
>(({ flows }): JSX.Element => {
  const [highlightedNodes, setHighlightedNodes] = useState<
    SubflowMessengerAction[]
  >([]);
  const { flowId } = useAppPathParams('/settings/dixa-messenger/flow/:flowId');
  const [navigateAwayPromptEnabled, setNavigateAwayPromptEnabled] =
    useState(true);

  const { getItem: fetchFlow, update: updateFlow, getItemAsyncStatus } = flows;

  const flowFetcher = useCallback(() => {
    if (flowId) {
      return fetchFlow(flowId);
    }
  }, [fetchFlow, flowId]);

  const {
    actions,
    active,
    messengerId,
    setActions,
    setActive,
    subflows,
    setSubflows,
    localCopyOfFlow,
    mainFlowSaveStatusData,
  } = useFlowData(flowFetcher);

  return (
    <FlowContext.Provider
      value={{
        initialMainFlow: localCopyOfFlow,
        actions: actions || [],
        active,
        messengerId,
        setActions,
        setActive,
        subflows: subflows || [],
        setSubflows,
        isLoading: getItemAsyncStatus === 'loading',
        flowId,
        update: updateFlow,
        mainFlowSaveStatusData,
        highlightedNodes,
        setHighlightedNodes,
        navigateAwayPromptEnabled,
        setNavigateAwayPromptEnabled,
      }}
    >
      <Outlet />
    </FlowContext.Provider>
  );
});
